import type { BusinessErrorReason } from './business-error-reason.js';
import type { TechnicalErrorVariant } from '../technical-error-variant.js';
import type { PinningError } from '../../pinning-module/pinning-error.js';
import type { AppointmentInfo } from '../../token-module/appointment-info.interface.js';

/**
 * Diskriminierte Union der fünf Cancel-Screens.
 *
 * Jeder Screen ist über das `screen`-Feld eindeutig identifiziert. Detail-
 * Felder (Reason / Variant) leben nur in den Cases, die sie tatsächlich
 * brauchen — wer im `BUSINESS_ERROR`-Branch ist, hat garantiert einen
 * `reason`, alle anderen Branches sehen das Feld gar nicht.
 *
 * `readonly` überall, weil der Reducer immer neue Objekte erzeugt und kein
 * Konsument den State mutieren darf.
 *
 * Im `TECHNICAL_ERROR`-Branch ist `pinningError` ein optionaler Sub-Code
 * (Pinning-spezifisch); er existiert nur bei `variant === 'pinning-failure'`
 * und wird in DEV-016 für Telemetrie ausgewertet — der CryptoKey selbst
 * lebt nicht im Reducer-State (nicht JSON-serialisierbar).
 *
 * `appointmentInfo` ist ab DEV-014 fester Bestandteil der Folge-States
 * (START → LOADING → SUCCESS / BUSINESS_ERROR). Sobald `TOKEN_PARSED` die
 * Info gesetzt hat, reicht der Reducer sie durch alle Übergänge weiter,
 * damit Loading-, Success- und Business-Error-Screen denselben Termin
 * anzeigen können wie der Start-Screen. `TECHNICAL_ERROR` führt die Info
 * bewusst nicht — der technische Fehler kann vor der Termin-Auflösung
 * eintreten, ein Termin-Teaser wäre dort irreführend.
 *
 * `dob` (LOADING) trägt das validierte Geburtsdatum bis zum API-Call
 * (DEV-015).
 */
export type CancelState =
  | { readonly screen: 'START'; readonly appointmentInfo?: AppointmentInfo }
  | {
      readonly screen: 'LOADING';
      readonly appointmentInfo?: AppointmentInfo;
      readonly dob?: string;
    }
  | { readonly screen: 'SUCCESS'; readonly appointmentInfo?: AppointmentInfo }
  | {
      readonly screen: 'BUSINESS_ERROR';
      readonly reason: BusinessErrorReason;
      readonly appointmentInfo?: AppointmentInfo;
    }
  | {
      readonly screen: 'TECHNICAL_ERROR';
      readonly variant: TechnicalErrorVariant;
      readonly pinningError?: PinningError;
    };

/**
 * Initialer Zustand der State-Machine: Start-Screen, kein Token verarbeitet,
 * keine Anfrage offen. `appointmentInfo` fehlt absichtlich — sie wird erst
 * nach `TOKEN_PARSED` gesetzt.
 */
export const INITIAL_CANCEL_STATE: CancelState = { screen: 'START' };
